import { PropsWithChildren } from "react"
import { Outlet } from "react-router-dom"
import styled from "styled-components"

import { useAppSelector } from "store/hooks"
import { contractorThemeSelector, verticalMenuOpenSelector } from "store/tempDataSlise"
import { ContractorThemes } from "constants/ContractorThemes"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { checkRoleType } from "utils/checkRoleType"
import { ContractorTopbar } from "components/topbars/ContractorTopbar"
import { SalesManagerTopbar } from "components/topbars/SalesManagerTopbar"
import { MOBILE_BREAKPOINT } from "components/topbars/styled"
import { VendorTopbar } from "components/topbars/VendorTopbar"
import { WerehouseTopbar } from "components/topbars/WerehouseTopbar"

import useUserProfileSync from "./useUserProfileSync"

const StyledPageWrapper = styled.section<{ backgroundStyle: string }>`
  min-height: 100vh;
  background: ${({ backgroundStyle }) => backgroundStyle};
  display: flex;
  flex-direction: column;
`

const ContentWrapper = styled.div<{ $paddingLeft: string; $paddingRight: string; $mobilePadding: string }>`
  padding-left: ${({ $paddingLeft }) => $paddingLeft};
  padding-right: ${({ $paddingRight }) => $paddingRight};
  transition: padding-left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: ${({ $mobilePadding }) => $mobilePadding};
  }
`

const MainLayout = ({ children }: PropsWithChildren) => {
  useUserProfileSync()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const userRole = checkRoleType(userProfile?.type)
  const hasEPlusPlan = userProfile?.contractor?.subscription_plan === SubscriptionPlan.E_PLUS
  const currentTheme = useAppSelector(contractorThemeSelector)
  const menuOpen = useAppSelector(verticalMenuOpenSelector)

  const isContractor = [
    "contractor-manager",
    "contractor-admin",
    "contractor-engineer",
    "contractor-technician",
  ].includes(userRole || "")

  const getBackgroundStyle = (hasEPlusPlan: boolean) => {
    if (hasEPlusPlan || !userProfile) {
      return currentTheme.backgroundGradient
    }
    return ContractorThemes.default.backgroundGradient
  }

  const setNecessaryTopbar = () => {
    if (isContractor) {
      return <ContractorTopbar />
    }
    if (userRole === "vendor-admin") {
      return <VendorTopbar />
    }
    if (userRole === "warehouse-manager") {
      return <WerehouseTopbar />
    }
    if (userRole === "sales-manager") {
      return <SalesManagerTopbar />
    }
    return null
  }

  const isAuthScreen = !userProfile

  const getContentPaddingLeft = () => {
    if (isAuthScreen) return "0"
    return menuOpen ? "232px" : "76px"
  }

  const paddingLeft = getContentPaddingLeft()
  const paddingRight = isAuthScreen ? "0" : "24px"
  const mobilePadding = isAuthScreen ? "0" : "0 16px"

  return (
    <StyledPageWrapper backgroundStyle={getBackgroundStyle(hasEPlusPlan)}>
      {setNecessaryTopbar()}
      <ContentWrapper $paddingLeft={paddingLeft} $paddingRight={paddingRight} $mobilePadding={mobilePadding}>
        {children}
        <Outlet />
      </ContentWrapper>
    </StyledPageWrapper>
  )
}

export default MainLayout
